import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CREATE_PRICE_RULE_MUTATION,
  PRICE_RULE_SUBJECTS_LOOKUP,
  UPDATE_PRICE_RULE_MUTATION,
} from './pricing.gql';

import { centsToMajorString, majorStringToCents } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface LookupsResponse {
  products: { items: { id: string; code: string; name: string }[] };
  services: { items: { id: string; code: string; name: string }[] };
  kits: { items: { id: string; code: string; name: string }[] };
  branches: { id: string; name: string }[];
}

interface PriceRuleFormDialogProps {
  rule?: EditablePriceRule | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

interface EditablePriceRule {
  id: string;
  subject_kind: string;
  subject_id: string;
  branch_id: string | null;
  priority: number;
  predicate_json: string;
  price_cents: string;
  note: string | null;
}

const PRESET_PREDICATES: { label: string; json: string }[] = [
  {
    label: 'Especie = perro',
    json: JSON.stringify({ op: 'eq', field: 'pet.species', value: 'dog' }, null, 2),
  },
  {
    label: 'Especie = gato',
    json: JSON.stringify({ op: 'eq', field: 'pet.species', value: 'cat' }, null, 2),
  },
  {
    label: 'Perro 1–5 kg',
    json: JSON.stringify(
      {
        op: 'and',
        clauses: [
          { op: 'eq', field: 'pet.species', value: 'dog' },
          { op: 'between', field: 'pet.current_weight_grams', min: 1000, max: 5000 },
        ],
      },
      null,
      2,
    ),
  },
  {
    label: 'Perro 5–10 kg',
    json: JSON.stringify(
      {
        op: 'and',
        clauses: [
          { op: 'eq', field: 'pet.species', value: 'dog' },
          {
            op: 'between',
            field: 'pet.current_weight_grams',
            min: 5000,
            max: 10000,
            inclusive: 'max',
          },
        ],
      },
      null,
      2,
    ),
  },
];

type PriceRuleSubjectKind = 'product' | 'service' | 'kit';
type PriceRuleSubjectKindGql = 'PRODUCT' | 'SERVICE' | 'KIT';

const PRICE_RULE_SUBJECT_KIND_GQL: Record<PriceRuleSubjectKind, PriceRuleSubjectKindGql> = {
  product: 'PRODUCT',
  service: 'SERVICE',
  kit: 'KIT',
};

function normalizeSubjectKind(kind: string | null | undefined): PriceRuleSubjectKind {
  const normalized = kind?.toLowerCase();
  if (normalized === 'product' || normalized === 'service' || normalized === 'kit') {
    return normalized;
  }
  return 'service';
}

export function PriceRuleFormDialog({
  rule,
  onClose,
  onSaved,
}: PriceRuleFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { data: lookups } = useQuery<LookupsResponse>(PRICE_RULE_SUBJECTS_LOOKUP, {
    fetchPolicy: 'cache-first',
  });
  const [createRule, { loading: creating }] = useMutation(CREATE_PRICE_RULE_MUTATION);
  const [updateRule, { loading: updating }] = useMutation(UPDATE_PRICE_RULE_MUTATION);
  const is_editing = Boolean(rule);
  const loading = creating || updating;

  const [subject_kind, setSubjectKind] = useState<PriceRuleSubjectKind>(
    normalizeSubjectKind(rule?.subject_kind),
  );
  const [subject_id, setSubjectId] = useState<string>(rule?.subject_id ?? '');
  const [branch_id, setBranchId] = useState<string>(rule?.branch_id ?? '');
  const [priority, setPriority] = useState<string>(String(rule?.priority ?? 0));
  const [price_major, setPriceMajor] = useState<string>(
    rule ? centsToMajorString(rule.price_cents) : '',
  );
  const [note, setNote] = useState<string>(rule?.note ?? '');
  const [predicate_json, setPredicateJson] = useState<string>(
    rule?.predicate_json ?? PRESET_PREDICATES[0]?.json ?? '',
  );
  const [error, setError] = useState<string | null>(null);

  const subjects =
    subject_kind === 'product'
      ? lookups?.products.items ?? []
      : subject_kind === 'service'
        ? lookups?.services.items ?? []
        : lookups?.kits.items ?? [];
  const subject_options =
    rule && subject_id && !subjects.some((s) => s.id === subject_id)
      ? [
          {
            id: subject_id,
            code: subject_id.slice(0, 8),
            name: t('pricing.current_subject'),
          },
          ...subjects,
        ]
      : subjects;

  const onSubmit = async (): Promise<void> => {
    setError(null);
    if (!subject_id) {
      setError(t('pricing.error_pick_subject'));
      return;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(price_major.trim())) {
      setError(t('pricing.error_invalid_price'));
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(predicate_json);
    } catch {
      setError(t('pricing.error_invalid_predicate'));
      return;
    }
    if (!parsed || typeof parsed !== 'object') {
      setError(t('pricing.error_invalid_predicate'));
      return;
    }
    const cents = majorStringToCents(price_major);
    if (cents < 0) {
      setError(t('pricing.error_invalid_price'));
      return;
    }
    const prio = Number.parseInt(priority, 10);
    try {
      const input = {
        priority: Number.isFinite(prio) && prio >= 0 ? prio : 0,
        predicate_json,
        price_cents: String(cents),
        note: note.trim() || null,
      };
      if (rule) {
        await updateRule({
          variables: {
            id: rule.id,
            input,
          },
        });
      } else {
        await createRule({
          variables: {
            input: {
              subject_kind: PRICE_RULE_SUBJECT_KIND_GQL[subject_kind],
              subject_id,
              branch_id: branch_id || null,
              ...input,
            },
          },
        });
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : is_editing ? 'update_failed' : 'create_failed');
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="max-h-[92vh] w-full max-w-3xl overflow-auto">
        <CardHeader>
          <CardTitle>{is_editing ? t('pricing.edit_rule') : t('pricing.new_rule')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('pricing.form_hint')}</p>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Label>{t('pricing.subject_kind')}</Label>
            <select
              value={subject_kind}
              onChange={(e) => {
                setSubjectKind(e.target.value as PriceRuleSubjectKind);
                setSubjectId('');
              }}
              disabled={is_editing}
              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
            >
              <option value="product">{t('pricing.kind_product')}</option>
              <option value="service">{t('pricing.kind_service')}</option>
              <option value="kit">{t('pricing.kind_kit')}</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label>{t('pricing.subject')}</Label>
            <select
              value={subject_id}
              onChange={(e) => setSubjectId(e.target.value)}
              disabled={is_editing}
              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
            >
              <option value="">{t('pricing.subject_placeholder')}</option>
              {subject_options.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>{t('pricing.branch')}</Label>
            <select
              value={branch_id}
              onChange={(e) => setBranchId(e.target.value)}
              disabled={is_editing}
              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
            >
              <option value="">{t('pricing.branch_global')}</option>
              {(lookups?.branches ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>{t('pricing.priority')}</Label>
            <Input
              type="number"
              min={0}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('pricing.price')}</Label>
            <Input
              inputMode="decimal"
              value={price_major}
              onChange={(e) => setPriceMajor(e.target.value)}
              placeholder="500.00"
            />
          </div>
          <div className="space-y-1">
            <Label>{t('pricing.note')}</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>{t('pricing.predicate')}</Label>
            <div className="mb-1 flex flex-wrap gap-1">
              {PRESET_PREDICATES.map((p) => (
                <Button
                  key={p.label}
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setPredicateJson(p.json)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <textarea
              className="min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
              value={predicate_json}
              onChange={(e) => setPredicateJson(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t('pricing.predicate_hint')}</p>
          </div>
          {error ? (
            <div className="md:col-span-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {error}
            </div>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onSubmit();
            }}
            disabled={loading}
          >
            {loading ? t('common.loading') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
